import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'react-hot-toast';
import { 
  Plus, Search, Eye, Loader2, AlertCircle, RefreshCw, 
  DollarSign, FileText, CheckCircle, XCircle, Clock 
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerOpenItems = () => {
  const navigate = useNavigate();
  const [openItems, setOpenItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [filters, setFilters] = useState({
    customer_id: '',
    status: '',
    include_cleared: false
  });
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    loadMasterData();
    loadOpenItems();
  // eslint-disable-next-line
  }, []);

  const loadMasterData = async () => {
    try {
      const custRes = await axios.get(`${BACKEND_URL}/api/customers`).catch(() => ({ data: [] }));
      setCustomers(custRes.data || []);
    } catch (error) {
      console.error('Error loading master data:', error);
    }
  };

  const loadOpenItems = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.customer_id) params.append('customer_id', filters.customer_id);
      if (filters.status) params.append('status', filters.status);
      if (filters.include_cleared) params.append('include_cleared', 'true');
      
      const response = await axios.get(`${BACKEND_URL}/api/ar/open-items?${params.toString()}`);
      setOpenItems(response.data || []);
    } catch (error) {
      console.error('Error loading open items:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load open items: ${errorMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/ar/open-items/sync`);
      toast.success(response.data.message || 'Open items synced successfully');
      loadOpenItems();
    } catch (error) {
      console.error('Error syncing open items:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to sync open items: ${errorMsg}`);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSearch = () => {
    loadOpenItems();
  };

  const handleClearFilters = () => {
    setFilters({ customer_id: '', status: '', include_cleared: false });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'OPEN': return 'bg-yellow-100 text-yellow-800';
      case 'PARTIALLY_CLEARED': return 'bg-orange-100 text-orange-800';
      case 'CLEARED': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'CLEARED': return <CheckCircle className="w-4 h-4" />;
      case 'PARTIALLY_CLEARED': return <Clock className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.customer_id === customerId);
    return customer?.customer_name || customer?.name || customerId;
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const totalOpen = openItems.reduce((sum, item) => sum + (item.open_amount || 0), 0);

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Customer Open Items</h1>
            <p className="text-sm text-gray-600 mt-1">Manage accounts receivable and customer balances</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleSync} disabled={isSyncing}>
              {isSyncing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Sync from Billing
            </Button>
            <Button onClick={() => navigate('/ar/payments/create')} data-testid="create-payment-btn">
              <Plus className="w-4 h-4 mr-2" />
              Post Payment
            </Button>
          </div>
        </div>

        {/* Summary Card */}
        <Card className="p-4 mb-6 bg-gradient-to-r from-orange-50 to-orange-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <DollarSign className="w-10 h-10 text-orange-600" />
              <div>
                <p className="text-sm text-gray-600">Total Open Receivables</p>
                <p className="text-2xl font-bold text-orange-800">
                  USD {totalOpen.toLocaleString(undefined, {minimumFractionDigits: 2})}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Open Items</p>
              <p className="text-xl font-bold text-gray-800">{openItems.length}</p>
            </div>
          </div>
        </Card>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-48">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Customer</label>
              <Select value={filters.customer_id || '__ALL__'} onValueChange={(v) => setFilters({...filters, customer_id: v === '__ALL__' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="All Customers" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All Customers</SelectItem>
                  {customers.map(c => (
                    <SelectItem key={c.customer_id} value={c.customer_id}>
                      {c.customer_id} - {c.customer_name || c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Status</label>
              <Select value={filters.status || '__ALL__'} onValueChange={(v) => setFilters({...filters, status: v === '__ALL__' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="All Statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All Statuses</SelectItem>
                  <SelectItem value="OPEN">Open</SelectItem>
                  <SelectItem value="PARTIALLY_CLEARED">Partially Cleared</SelectItem>
                  <SelectItem value="CLEARED">Cleared</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="include_cleared"
                checked={filters.include_cleared}
                onChange={(e) => setFilters({...filters, include_cleared: e.target.checked})}
                className="w-4 h-4"
              />
              <label htmlFor="include_cleared" className="text-sm text-gray-700">Include Cleared</label>
            </div>
            <Button onClick={handleSearch} data-testid="search-btn">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
            <Button variant="outline" onClick={handleClearFilters}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Clear
            </Button>
          </div>
        </Card>

        {/* Open Items Table */}
        <Card className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
              <span className="ml-3 text-gray-600">Loading open items...</span>
            </div>
          ) : openItems.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">No open items found.</p>
              <p className="text-sm text-gray-400 mt-1">Click "Sync from Billing" to import open items from released invoices.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="open-items-table">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Customer</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Invoice #</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Invoice Date</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Due Date</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Original</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Open</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {openItems.map((item) => (
                    <tr key={item.item_id} className={`border-b hover:bg-gray-50 ${isOverdue(item.due_date) && item.status !== 'CLEARED' ? 'bg-red-50' : ''}`}>
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium">{getCustomerName(item.customer_id)}</p>
                          <p className="text-xs text-gray-500">{item.customer_id}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span 
                          className="font-mono text-orange-600 cursor-pointer hover:underline"
                          onClick={() => navigate(`/otc/billing/${item.billing_number}`)}
                        >
                          {item.billing_number}
                        </span>
                      </td>
                      <td className="py-3 px-4">{item.billing_date?.split('T')[0]}</td>
                      <td className="py-3 px-4">
                        <span className={isOverdue(item.due_date) && item.status !== 'CLEARED' ? 'text-red-600 font-medium' : ''}>
                          {item.due_date?.split('T')[0]}
                        </span>
                        {isOverdue(item.due_date) && item.status !== 'CLEARED' && (
                          <span className="ml-2 text-xs text-red-600">(Overdue)</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right font-mono">
                        {item.currency} {item.original_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-bold">
                        {item.currency} {item.open_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit ${getStatusColor(item.status)}`}>
                          {getStatusIcon(item.status)}
                          {item.status?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        {item.status !== 'CLEARED' && (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => navigate(`/ar/payments/create?customer=${item.customer_id}&item=${item.item_id}`)}
                            title="Pay this item"
                          >
                            <DollarSign className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default CustomerOpenItems;
